/**
 * /community/squad/:squad_id — Squad detail with roster + feed.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, MapPin, Send, LogOut } from "lucide-react";
import { apiClient } from "../../services/api";
import { toast } from "sonner";

export default function SquadDetail() {
  const navigate = useNavigate();
  const { squad_id } = useParams();
  const [data, setData] = useState(null);
  const [posts, setPosts] = useState([]);
  const [text, setText] = useState("");

  const load = useCallback(() => {
    apiClient.get(`/community/squads/${squad_id}`).then((r) => setData(r.data));
  }, [squad_id]);
  const loadFeed = useCallback(() => {
    apiClient.get(`/community/squads/${squad_id}/feed`).then((r) => setPosts(r.data?.posts || [])).catch(() => setPosts([]));
  }, [squad_id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (data?.is_member) loadFeed(); }, [data, loadFeed]);

  if (!data) return <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg-base, #ECEAE6)" }}>Loading…</div>;
  const s = data.squad;

  const join = async () => { await apiClient.post(`/community/squads/${squad_id}/join`); toast.success("Joined squad · +10 FP"); load(); };
  const leave = async () => {
    if (!window.confirm("Leave this squad?")) return;
    await apiClient.delete(`/community/squads/${squad_id}/leave`);
    toast.success("You left the squad");
    navigate(-1);
  };
  const post = async () => {
    if (!text.trim()) return;
    try {
      await apiClient.post(`/community/squads/${squad_id}/feed`, { text });
      setText("");
      loadFeed();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="min-h-screen pb-32" style={{ background: "var(--bg-base, #ECEAE6)" }} data-testid="squad-detail">
      <header style={{ position: "sticky", top: 0, zIndex: 10, background: "var(--bg-elevated, #F0EEEA)", borderBottom: "1px solid var(--border, rgba(12,12,11,0.09))" }}>
        <div className="max-w-md mx-auto flex items-center px-4 py-3 gap-2">
          <button onClick={() => navigate(-1)} aria-label="Back" style={{ background: "transparent", border: "none" }}><ChevronLeft size={22} /></button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 800, lineHeight: 1.05 }}>{s.name}</div>
            <div style={{ fontSize: 11, color: "var(--text-tertiary, #8A8884)", display: "inline-flex", alignItems: "center", gap: 4 }}><MapPin size={10} /> {s.neighborhood} · {s.member_count} members</div>
          </div>
          {data.is_member ? (
            <button onClick={leave} aria-label="Leave" data-testid="leave-squad" style={{ background: "transparent", border: "1px solid var(--border, rgba(12,12,11,0.16))", borderRadius: 100, padding: "6px 10px", fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <LogOut size={11} /> Leave
            </button>
          ) : (
            <button onClick={join} data-testid="join-squad" style={{ background: "#0C0C0B", color: "white", border: "none", borderRadius: 100, padding: "6px 14px", fontSize: 12, fontWeight: 700 }}>Join</button>
          )}
        </div>
      </header>
      <main className="max-w-md mx-auto px-5 pt-4">
        {s.description && <p style={{ fontSize: 13, color: "var(--text-secondary, #4A4845)" }}>{s.description}</p>}

        <section style={{ marginTop: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary, #8A8884)" }}>This week's leaderboard</div>
          <div style={{ marginTop: 8, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12, overflow: "hidden" }}>
            {(data.members || []).slice(0, 10).map((m, i) => (
              <div key={m.user_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, borderBottom: i < 9 ? "1px solid var(--border, rgba(12,12,11,0.06))" : "none" }}>
                <span style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 900, color: i < 3 ? "#F5C518" : "var(--text-tertiary, #8A8884)", minWidth: 22 }}>#{i + 1}</span>
                <div style={{ width: 30, height: 30, borderRadius: "50%", background: "var(--bg-inset, #D8D6D1)", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 800 }}>{(m.name || "?").slice(0, 1).toUpperCase()}</div>
                <div style={{ flex: 1, fontSize: 13, fontWeight: 600 }}>{m.name || "Member"}{m.role === "owner" && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, color: "#A37B00", textTransform: "uppercase" }}>OWNER</span>}</div>
                <div style={{ fontFamily: "Barlow Condensed", fontSize: 18, fontWeight: 900 }}>{m.weekly_score || 0}</div>
              </div>
            ))}
          </div>
        </section>

        {data.is_member && (
          <section style={{ marginTop: 22 }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-tertiary, #8A8884)", marginBottom: 8 }}>Squad chat</div>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <input value={text} onChange={(e) => setText(e.target.value.slice(0, 500))} placeholder="Share with your squad…" data-testid="squad-post-input"
                style={{ flex: 1, height: 40, padding: "0 14px", background: "white", border: "1px solid var(--border, rgba(12,12,11,0.16))", borderRadius: 100, fontSize: 13 }} />
              <button onClick={post} disabled={!text.trim()} data-testid="squad-post-send"
                style={{ width: 40, height: 40, borderRadius: "50%", background: "#0C0C0B", color: "white", border: "none", display: "grid", placeItems: "center" }}>
                <Send size={14} />
              </button>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {posts.length === 0 ? (
                <p style={{ fontSize: 12, color: "var(--text-tertiary, #8A8884)", textAlign: "center", padding: 20 }}>No squad posts yet.</p>
              ) : posts.map((p) => (
                <div key={p.post_id} style={{ padding: 12, background: "var(--bg-surface, #E4E2DE)", border: "1px solid var(--border, rgba(12,12,11,0.10))", borderRadius: 12 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 28, height: 28, borderRadius: "50%", background: "var(--bg-inset, #D8D6D1)", display: "grid", placeItems: "center", fontSize: 11, fontWeight: 700 }}>{(p.user_name || "?").slice(0, 1).toUpperCase()}</div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{p.user_name}</div>
                    <div style={{ fontSize: 10, color: "var(--text-tertiary, #8A8884)" }}>· {new Date(p.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</div>
                  </div>
                  <p style={{ fontSize: 14, marginTop: 6 }}>{p.text}</p>
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
